/**
 * CharterClassLanding — the student's entry point for a standalone Charter
 * Class. Replaces the game waiting room, which assumes a game that a class
 * does not have.
 *
 * Resolves where the student belongs from the charter session and forwards:
 *   no team yet       → wait here, with individual prep available
 *   round not started → wait here
 *   phase 1           → CharterPrepPage
 *   phase 2 / locked  → CharterGroupPage
 *
 * It also hydrates `currentRoom`, which the charter WebSocket processors read
 * (without a fallback) when refetching the session after a professor action.
 */
import { useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { GraduationCap, Loader2, Users } from 'lucide-react'

import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { getRoomDetails } from '@/redux/asyncThunks/room'
import ROUTES from '@/routes/routes'
import { fetchCharterSession } from '@/features/charter/redux/asyncThunks/charterThunks'
import { selectCharterSession } from '@/features/charter/redux/selectors/charterSelectors'
import { setSession } from '@/features/charter/redux/slices/charterSlice'
import { useMyPrepStatus } from '@/features/charter/hooks/useMyPrepStatus'
import { PreTeamPrepCard } from '@/features/charter/components/Shared/PreTeamPrepCard'

/** How often to re-check while the student is waiting for a team/round. */
const POLL_MS = 5000

export default function CharterClassLanding() {
  const { roomId = '' } = useParams<{ roomId: string }>()
  const [searchParams] = useSearchParams()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const currentRoom = useAppSelector((state) => state.room.currentRoom)
  const session = useAppSelector(selectCharterSession)

  const inviteCode = searchParams.get('inviteCode') || currentRoom?.invite_code || ''

  useEffect(() => {
    if (roomId && currentRoom?.roomid !== roomId) {
      dispatch(getRoomDetails(roomId))
    }
  }, [dispatch, roomId, currentRoom?.roomid])

  // The charter slice is global and the session payload carries no room id, so
  // clear it first: a session left over from another class would otherwise
  // forward this student using the wrong team.
  useEffect(() => {
    if (roomId) dispatch(setSession(null))
  }, [dispatch, roomId])

  const hasTeam = Boolean(session?.team_id)
  // Prep status answers before placement; the session endpoint 404s until then.
  const prepStatus = useMyPrepStatus(roomId, !hasTeam)
  const placed = prepStatus?.has_team === true

  // Poll once placed: the professor starts the round and moves the team on,
  // and neither pushes an event to this page.
  useEffect(() => {
    if (!roomId || !placed) return
    dispatch(fetchCharterSession({ roomId }))
    const id = setInterval(() => {
      dispatch(fetchCharterSession({ roomId }))
    }, POLL_MS)
    return () => clearInterval(id)
  }, [dispatch, roomId, placed])

  // Forward as soon as the session says where this student belongs.
  useEffect(() => {
    if (!session?.team_id) return
    const query = `?inviteCode=${inviteCode}&teamId=${session.team_id}`

    if (session.phase === 'phase1_prep' && session.status === 'in_progress') {
      navigate(ROUTES.STUDENT.CHARTER_PREP(roomId) + query, { replace: true })
      return
    }
    if (session.phase === 'phase2_group' || session.phase === 'ratified') {
      navigate(ROUTES.STUDENT.CHARTER_GROUP(roomId) + query, { replace: true })
    }
  }, [
    session?.team_id,
    session?.phase,
    session?.status,
    inviteCode,
    roomId,
    navigate,
  ])

  const waitingForRound = hasTeam && session?.status === 'not_started'

  return (
    <div className='flex justify-center items-center px-6 min-h-[70vh]'>
      <div className='w-full max-w-lg text-center'>
        <div className='inline-flex justify-center items-center mb-4 w-14 h-14 bg-indigo-50 rounded-full'>
          <GraduationCap className='w-7 h-7 text-indigo-500' />
        </div>

        <h1 className='text-xl font-semibold text-dark-blue'>
          {currentRoom?.roomname || 'Charter Class'}
        </h1>

        {prepStatus?.has_team === false ? (
          <div className='mt-4'>
            <PreTeamPrepCard roomId={roomId} inviteCode={inviteCode} status={prepStatus} />
          </div>
        ) : waitingForRound ? (
          <>
            <div className='flex gap-2 justify-center items-center mt-3 text-gray-600'>
              <Users className='w-4 h-4 text-indigo-500' />
              <span>You&apos;re on a team.</span>
            </div>
            <p className='mt-1 text-sm text-gray-400'>
              Waiting for your instructor to start the charter session.
            </p>
          </>
        ) : (
          <div className='flex gap-2 justify-center items-center mt-3 text-gray-500'>
            <Loader2 className='w-4 h-4 animate-spin' />
            <span>Opening your charter session…</span>
          </div>
        )}
      </div>
    </div>
  )
}
